import React, {
    forwardRef,
    useEffect,
    useState,
    useRef,
    useCallback,
    useMemo,
    useImperativeHandle,
} from "react";
import styles from "./LayoutEditComp.module.scss";
import Icon from "../../Shared/Icon/Icon";
import Button, { ButtonTypes } from "../../Shared/Button/Button";
import { getRandomId, windowDimensions } from "../../utils/globalUtils";
import PopupTargettingParams from "../PopupTargettingParams/PopupTargettingParams";
import Select from "../../Shared/Select/Select";
import Input from "../../Shared/Input/Input";
import RichTextBox from "../../Shared/RichTextBox/RichTextBox";
import { Devices } from "../Devices/Devices";
import {
    DeviceTypes,
    DeviceIconMap,
} from "../../constants/creativePreviewConstants";
import ToggleValueSwitch from "../../Shared/ToggleValueSwitch/ToggleValueSwitch";
import Tooltip from "../../Shared/Tooltip/Tooltip";
import OutsideClickHandler from "react-outside-click-handler";
import ReactSelect from "../../Shared/ReactSelect/ReactSelect";
import InfoText from "../../Shared/InfoText/InfoText";
import { isEmpty, debounce } from "lodash";
import {
    ADS,
    PREVIEW_LINK_URL,
    SITE_CONFIG,
    TEMPLATE,
} from "../../constants/url";
import { reactQueryApiCall } from "../../generalApi/generalApi";
import ToggleSwitch from "../../Shared/ToggleSwitch/ToggleSwitch";
import SpinnerLoader from "../../Shared/SpinnerLoader/SpinnerLoader";
import { SelectWithCloneOption } from "../../Pages/CreateContainer/CreateContainer";
import { useDispatch, useSelector } from "react-redux";
import CreateTemplate, {
    TemplateKeys,
} from "../../Pages/CreateTemplate/CreateTemplate";
import {
    cloneTemplate,
    fetchAllTemplate,
    fetchTemplate,
    saveKeyword,
    templateActions,
    fetchKeyword,
} from "../../store/flowmanager/template";
import SidePanelInfinity from "../../Shared/SidePanelInfinity/SidePanelInfinity";
import { toastListActions } from "../../store/toastList/toastList";
import { useQueryClient } from "react-query";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import nothingFound from "../../assets/img/nothingFound.svg";
import noTemplate from "../../assets/img/noTemplate.svg";

import customFetch from "../../fetch/customFetch";
import TargettingParamsWrapperComp from "../../Shared/TargettingParamsWrapperComp/TargettingParamsWrapperComp";
import InputList from "../InputList/InputList";
import CustomTooltipRenderer from "../Renderers/CustomTooltipRenderer/CustomTooltipRenderer";
import ActionButtons from "../Renderers/ActionButtons/ActionButtons";
import Table from "../../Shared/Table/Table";
import TextRenderer from "../Renderers/TextRenderer/TextRenderer";

const LayoutEditComp = forwardRef(
    (
        {
            defaultConfigs = [],
            individualLayoutData = [],
            item = {},
            updatedLayout,
            ondelete,
            showConfigSelect,
            addConfigList = [],
            setShowConfigSelect,
            customOnSelect,
            onCancelHandler,
            showDefaultMsg = false,
            showitemsDropdown = [],
            itemsDropdownId = null,
            onChangeItem,
            readOnly,
            hidePreviewHeader = false,
            selectedFlowDomain = null,
            showTemplate = false,
            selectedTemplateId = null,
            vertical = "",
        },
        ref
    ) => {
        const [active, setActive] = useState(DeviceTypes[0]);
        const [isCollapsed, setIsCollapsed] = useState(false);
        const [confirmPopupKey, setConfirmPopupKey] = useState("");
        const defaultConfigKey = useMemo(
            () => defaultConfigs.map(_ => _.key),
            [defaultConfigs]
        );
        const [domainType, setDomainType] = useState({});
        const [iframeSrc, setIframeSrc] = useState("");
        const [randomKey, setRandomKey] = useState(0);
        const [autoReload, setAutoReload] = useState(true);
        const [iframeStyle, setIframeStyle] = useState({ display: "none" });
        const [iframeLoader, setIframeLoader] = useState(false);

        const [headerTitle, setHeaderTitle] = useState("Layout Features");
        const [templateId, setTemplateId] = useState(null);
        const [viewIsClick, setViewIsClick] = useState(false);
        const [createPhasePopup, setCreatePhasePopup] = useState(false);
        const [templateCloneObj, setTemplateCloneObj] = useState(null);
        const [loading, setLoading] = useState(false);

        const iframeContainer = useRef(null);
        const templateIframe = useRef(null);

        const dispatch = useDispatch();

        const queryClient = useQueryClient();
        const templateOpenRef = useRef();
        const saveFunctionCallRef = useRef(null);

        const isBigScreen = windowDimensions().height > 900 ? true : false;

        const { apiData: apiTemplateData, isLoading: templateLoader } =
            reactQueryApiCall("templateListData", "Template List", {
                url: TEMPLATE.FETCH_ALL_TEMPLATE_LIST,
                method: "GET",
            });

        const templates = useMemo(() => {
            return apiTemplateData || [];
        }, [apiTemplateData]);

        const createTemplateState = useSelector(
            state => state.flowmanager.template.createTemplate
        );

        // Fetching general api using react-query
        const adUnitData = reactQueryApiCall("adUnitListData", "Ad unit List", {
            url: ADS.FETCH_AD_UNITS,
            method: "POST",
        });

        const { apiData: apiSiteConfigData, isLoading: siteConfigLoader } =
            reactQueryApiCall("siteConfigListData", "SiteConfig List", {
                url: SITE_CONFIG.GET_ALL_CONFIGS,
                method: "POST",
            });

        const siteConfigList = useMemo(() => {
            return vertical === "" || vertical == null
                ? apiSiteConfigData?.data || []
                : apiSiteConfigData?.data?.filter(
                      ele => ele.vertical === vertical
                  );
        }, [apiSiteConfigData, vertical]);

        const layoutTypeConstant = [
            {
                id: "Layout Features",
                type: "Layout Features",
                icon: "LayoutConfig",
            },
            {
                id: "Advance Features",
                type: "Advance Features",
                icon: "AdvancedConfig",
            },
        ];

        const debounceFn = useCallback(debounce(changeLayoutConfigData, 300), [
            individualLayoutData,
        ]);

        function changeLayoutConfigData(obj, val, type = "", dataType = "") {
            let copyLayoutData = JSON.parse(
                JSON.stringify(individualLayoutData)
            );

            if (type === "keyword") {
                if (dataType === "id") {
                    const backgroundKeyExists =
                        copyLayoutData[0].configurations.some(
                            item => item.key === "backgroundStaticListings"
                        );

                    if (backgroundKeyExists) {
                        copyLayoutData[0].configurations.forEach(
                            (item, idx) => {
                                if (item.key === "backgroundStaticListings") {
                                    item.data = val;
                                }
                            }
                        );
                    } else {
                        copyLayoutData[0].configurations.push({
                            key: "backgroundStaticListings",
                            data: val,
                        });
                    }
                } else {
                    if (dataType === "keywordsRemoved") {
                        const findIndex =
                            copyLayoutData[0].configurations.findIndex(
                                a => a.key === "backgroundStaticListings"
                            );

                        findIndex !== -1 &&
                            copyLayoutData[0].configurations.splice(
                                findIndex,
                                1
                            );
                    } else {
                        copyLayoutData[0].configurations.forEach(
                            (item, idx) => {
                                if (item.key === obj.key) {
                                    item.data = val;
                                }
                            }
                        );
                    }
                }
            } else {
                copyLayoutData[0].configurations.forEach((item, idx) => {
                    if (item.key === obj.key) {
                        item.data = val;
                    }
                });
            }

            updatedLayout(copyLayoutData);
        }

        // useEffect(() => {
        //     dispatch(templateActions.setLoading(templateLoader));
        // }, [templateLoader]);

        useEffect(() => {
            if (!isEmpty(item) && !isEmpty(domainType)) {
                if (autoReload) {
                    loadIframe(item, active);
                }
            }
        }, [domainType, item]);

        useEffect(() => {
            if (siteConfigList?.length > 0) {
                setDomainType(
                    selectedFlowDomain !== null
                        ? siteConfigList.find(
                              (obj, idx) => obj.domain === selectedFlowDomain
                          ) || siteConfigList[0]
                        : siteConfigList[0]
                );
            }
        }, [selectedFlowDomain, siteConfigList]);

        useEffect(() => {
            if (
                (individualLayoutData[0]?.type === "REGISTER" ||
                    individualLayoutData[0]?.type === "DETAILS_CONFIRMATION") &&
                !hidePreviewHeader
            ) {
                setTimeout(() => {
                    setAutoReload(false);
                }, 2000);
            }
        }, []);

        const loadIframe = async (item, deviceType) => {
            const type = item?.rule?.container?.type;
            if (
                type === "REGISTER" ||
                type === "SURVEY" ||
                type === "DETAILS_CONFIRMATION"
            ) {
                setIframeLoader(true);
                setIframeStyle({ display: "none" });
                const previewCallResponse = await customFetch(
                    `${PREVIEW_LINK_URL.previewLink}?domain=${domainType.productShortCode}&deviceType=${deviceType}`,
                    "POST",
                    item,
                    {},
                    {},
                    null,
                    null,
                    data => {
                        dispatch(
                            toastListActions.setToastList({
                                type: "Error",
                                message: "Failed to load Preview.",
                            })
                        );
                    }
                );
                setIframeSrc(previewCallResponse?.data?.data?.url);
                setRandomKey(getRandomId());
                setTimeout(() => {
                    setIframeLoader(false);
                    setIframeStyle({
                        display: "block",
                    });
                }, 1500);
            }
        };

        useEffect(() => {
            setTemplateId(selectedTemplateId);
        }, [selectedTemplateId]);

        useImperativeHandle(ref, () => {
            return {
                getData: () => {
                    return {
                        templateId,
                        templateLoader,
                        templateList: templates,
                    };
                },
            };
        });

        return (
            <div
                className={`${styles.layoutPopupContainer} ${
                    hidePreviewHeader
                        ? styles.layoutPopupContainerPreviewNode
                        : ""
                }`}
            >
                {(defaultConfigs?.length > 0 || showTemplate) && (
                    <div className={styles.layoutConfigWrapper}>
                        {showTemplate && (
                            <div className={styles.leftPanelWrapper}>
                                {layoutTypeConstant.map((ele, idx) => {
                                    return (
                                        <div
                                            className={`${styles.iconWrapper} ${
                                                headerTitle === ele.type &&
                                                styles.iconWrapperActive
                                            }`}
                                            onClick={() =>
                                                setHeaderTitle(ele.type)
                                            }
                                        >
                                            <Tooltip
                                                message={ele.type}
                                                position="right"
                                            >
                                                <Icon
                                                    icon={ele.icon}
                                                    size={"18px"}
                                                />
                                            </Tooltip>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <div
                            className={`${styles.dataWrapper} ${
                                !showTemplate && "pt-20"
                            } ${showTemplate && styles.dataWrapperPadding}`}
                        >
                            {showTemplate && (
                                <div className={styles.headerTitle}>
                                    {headerTitle}
                                </div>
                            )}
                            {headerTitle === "Layout Features" &&
                                defaultConfigs?.length !== 0 && (
                                    <div className="relative mr-20 mb-20">
                                        <Button
                                            btnTheme={
                                                ButtonTypes.primaryHover_btn
                                            }
                                            onClick={() => {
                                                setShowConfigSelect(true);
                                            }}
                                            disable={
                                                addConfigList.length === 0 ||
                                                readOnly
                                            }
                                        >
                                            Add Feature
                                        </Button>

                                        <OutsideClickHandler
                                            onOutsideClick={() =>
                                                setShowConfigSelect(false)
                                            }
                                        >
                                            {showConfigSelect &&
                                                addConfigList.length > 0 && (
                                                    <div
                                                        className={
                                                            styles.configSelectWrap
                                                        }
                                                    >
                                                        <ReactSelect
                                                            placeholder="Search for config"
                                                            isMulti
                                                            controlShouldRenderValue={
                                                                false
                                                            }
                                                            maxMenuHeight={260}
                                                            menuIsOpen
                                                            options={
                                                                addConfigList
                                                            }
                                                            listSelector={"key"}
                                                            searchIdField={
                                                                "key"
                                                            }
                                                            selectedIds={[]}
                                                            onCancelHandler={e => {
                                                                onCancelHandler();
                                                            }}
                                                            showSelectAll={
                                                                addConfigList.length >
                                                                5
                                                            }
                                                            customOnSelect={item => {
                                                                customOnSelect(
                                                                    item
                                                                );
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                        </OutsideClickHandler>
                                    </div>
                                )}
                            {headerTitle === "Advance Features" && (
                                <>
                                    <div
                                        className={`form-row ${styles.templateWrapper}`}
                                    >
                                        <div
                                            className={`dp-parent headerTitleRequired maxChildContainer dp-parent-hor-space-between`}
                                        >
                                            Feature Group
                                            {templateId !== null && (
                                                <span
                                                    className={
                                                        styles.editWrapper
                                                    }
                                                    onClick={() => {
                                                        setTimeout(() => {
                                                            templateOpenRef.current.open();

                                                            dispatch(
                                                                fetchTemplate(
                                                                    templateId
                                                                )
                                                            );
                                                        });

                                                        setViewIsClick(true);
                                                    }}
                                                >
                                                    <Icon
                                                        icon={"Edit"}
                                                        size="14px"
                                                        enableHover={true}
                                                    />
                                                    Edit
                                                </span>
                                            )}
                                        </div>
                                        <p className="infoText">
                                            Type associated with a Layout
                                        </p>
                                        <Select
                                            showSearch={true}
                                            showCreate={true}
                                            onCreate={item => {
                                                setCreatePhasePopup(true);
                                                templateOpenRef.current.open();
                                                setTimeout(() => {
                                                    dispatch(
                                                        templateActions.setTemplateName(
                                                            item.name
                                                        )
                                                    );
                                                });
                                            }}
                                            loading={templateLoader || loading}
                                            label={"Select Feature Group"}
                                            options={templates}
                                            keyField={"name"}
                                            onSelect={item => {
                                                setTemplateId(item?.id || null);
                                            }}
                                            idField={"id"}
                                            onLoadSelectReturn={false}
                                            selectedId={templateId}
                                            isDisabled={readOnly}
                                            closeIcon={true}
                                            CustomRender={option => (
                                                <SelectWithCloneOption
                                                    option={option.option}
                                                    selector="name"
                                                    onClone={() => {
                                                        setTimeout(() => {
                                                            setTemplateCloneObj(
                                                                option.option
                                                            );

                                                            templateOpenRef.current.open();

                                                            dispatch(
                                                                fetchTemplate(
                                                                    option
                                                                        .option
                                                                        .id
                                                                )
                                                            );
                                                        });
                                                    }}
                                                />
                                            )}
                                            portal={false}
                                        />
                                    </div>

                                    {templateId === null ? (
                                        <div
                                            className={
                                                styles.noSelectionWrapper
                                            }
                                        >
                                            <div>
                                                <img src={noTemplate} />
                                            </div>
                                            <div
                                                className={styles.selectonText}
                                            >
                                                No Selection yet!
                                            </div>
                                            <div className={styles.content}>
                                                Looks like you haven’t selected
                                                any Feature Group yet.
                                                <br /> Click on the Feature
                                                Group dropdown to select or
                                                create a Feature Group
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            className={
                                                styles.dataWrapperTemplate
                                            }
                                        >
                                            {!templateLoader ? (
                                                templates
                                                    .filter(
                                                        obj =>
                                                            obj.id ===
                                                            templateId
                                                    )
                                                    .map((item, idx) => {
                                                        const itemKeys =
                                                            Object.keys(
                                                                item.properties
                                                            );

                                                        const isPResentKey =
                                                            TemplateKeys.some(
                                                                element =>
                                                                    itemKeys.includes(
                                                                        element.name
                                                                    )
                                                            );

                                                        return (
                                                            <>
                                                                <div
                                                                    className={`form-row maxChildContainer`}
                                                                >
                                                                    <p className="headerTitleRequired">
                                                                        ID
                                                                    </p>
                                                                    <p>
                                                                        {
                                                                            item.id
                                                                        }
                                                                    </p>
                                                                </div>
                                                                <div
                                                                    className={`form-row maxChildContainer`}
                                                                >
                                                                    <p className="headerTitleRequired">
                                                                        Features
                                                                    </p>
                                                                    {itemKeys.map(
                                                                        (
                                                                            _,
                                                                            idx
                                                                        ) => {
                                                                            const titleName =
                                                                                TemplateKeys.filter(
                                                                                    obj =>
                                                                                        obj.name ===
                                                                                        _
                                                                                );

                                                                            return (
                                                                                <>
                                                                                    {titleName?.length >
                                                                                        0 && (
                                                                                        <div
                                                                                            className={
                                                                                                styles.boxWrapper
                                                                                            }
                                                                                        >
                                                                                            <div
                                                                                                className={
                                                                                                    styles.title
                                                                                                }
                                                                                            >
                                                                                                {titleName[0]
                                                                                                    ?.opsName ||
                                                                                                    _}
                                                                                            </div>
                                                                                            <div
                                                                                                className={
                                                                                                    styles.value
                                                                                                }
                                                                                            >
                                                                                                {
                                                                                                    item
                                                                                                        ?.properties[
                                                                                                        _
                                                                                                    ]
                                                                                                        ?.name
                                                                                                }
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                </>
                                                                            );
                                                                        }
                                                                    )}

                                                                    {!isPResentKey && (
                                                                        <div
                                                                            className={`${styles.boxWrapper} ${styles.noConfig}`}
                                                                        >
                                                                            {" "}
                                                                            No
                                                                            Feature
                                                                            keys
                                                                            selected{" "}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </>
                                                        );
                                                    })
                                            ) : (
                                                <>
                                                    <div
                                                        className={`form-row maxChildContainer`}
                                                    >
                                                        <Skeleton
                                                            width={335}
                                                            height={60}
                                                        />
                                                    </div>
                                                    <div
                                                        className={`form-row maxChildContainer`}
                                                    >
                                                        <Skeleton
                                                            width={335}
                                                            height={60}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                            {headerTitle === "Layout Features" ? (
                                individualLayoutData[0]?.configurations
                                    ?.length > 0 ? (
                                    individualLayoutData[0]?.configurations
                                        ?.filter(
                                            _ =>
                                                !_.toDelete &&
                                                _.key !==
                                                    "backgroundStaticListings"
                                        )
                                        ?.map((element, idx) => {
                                            let mergedChoicesSet = new Set(
                                                Array.isArray(element?.data)
                                                    ? [
                                                          ...(element?.choices
                                                              ? element.choices
                                                              : []),
                                                          ...element?.data,
                                                      ]
                                                    : [
                                                          ...(element?.choices
                                                              ? element.choices
                                                              : []),
                                                          element?.data,
                                                      ]
                                            );

                                            return (
                                                <div
                                                    className={
                                                        styles.boxWrapper
                                                    }
                                                    key={idx}
                                                >
                                                    <div
                                                        className={
                                                            styles.titleWrapper
                                                        }
                                                    >
                                                        <div>Key :&nbsp;</div>
                                                        <div>{element.key}</div>
                                                    </div>
                                                    <div
                                                        className={`${styles.titleWrapper} ${styles.titleWrapperSecond}`}
                                                    >
                                                        <div>
                                                            Description :&nbsp;
                                                        </div>
                                                        <div>
                                                            {
                                                                element.description
                                                            }
                                                        </div>
                                                    </div>

                                                    {defaultConfigKey.includes(
                                                        element.key
                                                    ) ? (
                                                        <DisplayDataType
                                                            element={element}
                                                            mergedChoicesSet={
                                                                mergedChoicesSet
                                                            }
                                                            debounceFn={
                                                                debounceFn
                                                            }
                                                            adUnitData={
                                                                adUnitData
                                                            }
                                                            readOnly={readOnly}
                                                            individualLayoutData={
                                                                individualLayoutData
                                                            }
                                                        />
                                                    ) : (
                                                        <>
                                                            <div
                                                                className={`${styles.titleWrapper} ${styles.titleWrapperNoWidth}`}
                                                            >
                                                                <div>
                                                                    Value
                                                                    :&nbsp;
                                                                </div>
                                                                <div>
                                                                    {element?.data?.toString()}
                                                                </div>
                                                            </div>
                                                            <div
                                                                className={`warningBox dp-parent dp-parent-ver-center gap-10`}
                                                            >
                                                                <Icon
                                                                    icon="Error"
                                                                    size="16px"
                                                                    color="#E76E6E"
                                                                />
                                                                <p>
                                                                    This
                                                                    configuration
                                                                    has been
                                                                    deleted from
                                                                    the layout.
                                                                </p>
                                                            </div>
                                                        </>
                                                    )}

                                                    {/* {!defaultConfigKey.includes(
                                        element.key
                                    ) && (
                                        <p className="textError">
                                            *This configuration has been deleted
                                            from the layout.
                                        </p>
                                    )} */}
                                                    {!readOnly && (
                                                        <div
                                                            className={`${
                                                                styles.deleteIcon
                                                            } ${
                                                                element.required
                                                                    ? styles.deleteIcon__disable
                                                                    : "cursorPointer"
                                                            }`}
                                                            onClick={() =>
                                                                !element.required &&
                                                                setConfirmPopupKey(
                                                                    element.key
                                                                )
                                                            }
                                                        >
                                                            <Tooltip
                                                                message={
                                                                    element.required
                                                                        ? "Feature is mandatory."
                                                                        : "Remove Feature"
                                                                }
                                                                position="left"
                                                            >
                                                                <Icon
                                                                    icon={
                                                                        "DeleteFilled"
                                                                    }
                                                                    size={
                                                                        "18px"
                                                                    }
                                                                    className="circleIconColor circleIconColor--delete"
                                                                    disable={
                                                                        element.required
                                                                    }
                                                                />
                                                            </Tooltip>
                                                        </div>
                                                    )}
                                                    {confirmPopupKey ===
                                                        element.key && (
                                                        <div
                                                            className={
                                                                styles.deletePopup
                                                            }
                                                        >
                                                            <PopupTargettingParams
                                                                setShowPopup={() =>
                                                                    setConfirmPopupKey(
                                                                        ""
                                                                    )
                                                                }
                                                                Rfunction={() => {
                                                                    // debounceDeleteFn(element);
                                                                    ondelete(
                                                                        element
                                                                    );
                                                                }}
                                                                customText={`This will delete this layout configuration. Do you wish to proceed?`}
                                                                readOnly={
                                                                    readOnly
                                                                }
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                ) : (
                                    <div className={styles.noSelectionWrapper}>
                                        <div>
                                            <img src={nothingFound} />
                                        </div>
                                        <div className={styles.selectonText}>
                                            No Config Found!
                                        </div>
                                        <div className={styles.content}>
                                            Looks like no configurations are
                                            available under this container
                                            layout.
                                        </div>
                                    </div>
                                )
                            ) : (
                                ""
                            )}
                        </div>
                    </div>
                )}

                {iframeSrc && (
                    <div
                        className={`${styles.preview_container} ${
                            hidePreviewHeader ? styles.previewNodeContainer : ""
                        }`}
                    >
                        <div
                            className={`row ${
                                hidePreviewHeader
                                    ? styles.buttonGroupHeaderHide
                                    : ""
                            }`}
                        >
                            <div className={`${styles.buttonGroupHeader}`}>
                                <div className={`${styles.buttonGroup}`}>
                                    {DeviceTypes.map(type => (
                                        <div
                                            className={`${styles.tab} ${
                                                type === active
                                                    ? styles.active
                                                    : ""
                                            }`}
                                            key={type}
                                            onClick={() => {
                                                setActive(type);
                                                loadIframe(item, type);
                                            }}
                                        >
                                            <Icon
                                                icon={
                                                    type === active
                                                        ? `${DeviceIconMap[type]}Filled`
                                                        : DeviceIconMap[type]
                                                }
                                                size={"18px"}
                                                enableHover={true}
                                                className="mr-5"
                                            />{" "}
                                            {type}
                                        </div>
                                    ))}
                                </div>

                                <div
                                    className={`dp-parent gap-20 ${styles.previewUpDateBtn}`}
                                >
                                    <div
                                        className={`dp-parent gap-10 ${styles.autoReload}`}
                                    >
                                        <div>Auto Reload</div>
                                        <div>
                                            <ToggleSwitch
                                                isToggleOn={autoReload}
                                                setIsToggleOn={val => {
                                                    setAutoReload(val);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <Select
                                        className={styles.domainDropdown}
                                        options={siteConfigList}
                                        keyField={"productShortCode"}
                                        idField={"domain"}
                                        onSelect={data => {
                                            setDomainType(data);
                                        }}
                                        loading={siteConfigLoader}
                                        onLoadSelectReturn={false}
                                        selectedId={domainType.domain}
                                    />

                                    {showitemsDropdown?.length > 1 && (
                                        <Select
                                            options={showitemsDropdown}
                                            keyField={"name"}
                                            idField={"id"}
                                            selectedId={itemsDropdownId}
                                            onSelect={data => {
                                                onChangeItem(data);
                                            }}
                                            onLoadSelectReturn={false}
                                            showOptionToolTip={false}
                                        />
                                    )}

                                    <div>
                                        <Tooltip
                                            message="Reload preview"
                                            customClass={"reloadTooltip"}
                                            position="bottom"
                                        >
                                            <Button
                                                className={`${styles.refreshButton} flex`}
                                                onClick={() => {
                                                    loadIframe(item, active);
                                                }}
                                                btnTheme={
                                                    ButtonTypes.default_btn
                                                }
                                                btnWithIcon={true}
                                            >
                                                <div
                                                    className={`flex ${styles.icon}`}
                                                >
                                                    <Icon
                                                        icon="Refresh"
                                                        size="18px"
                                                    />
                                                </div>
                                            </Button>
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className={styles.previewDeviceContainer}>
                                <div
                                    className={`${styles.fullHeight} ${
                                        hidePreviewHeader
                                            ? styles.fullHeightNodePreview
                                            : ""
                                    }`}
                                >
                                    <div
                                        className={`${styles.imgContainer} ${
                                            isBigScreen
                                                ? styles.isBigScreen
                                                : ""
                                        } ${
                                            hidePreviewHeader
                                                ? styles.imgContainerNodePreview
                                                : ""
                                        }`}
                                        ref={iframeContainer}
                                    >
                                        <Devices
                                            use={active}
                                            retrigger={isCollapsed}
                                        >
                                            <>
                                                {iframeLoader && (
                                                    <div
                                                        className={
                                                            styles.loaderWrapper
                                                        }
                                                    >
                                                        <SpinnerLoader />
                                                    </div>
                                                )}

                                                <iframe
                                                    style={iframeStyle}
                                                    key={randomKey}
                                                    height="100%"
                                                    width="100%"
                                                    src={iframeSrc}
                                                    ref={templateIframe}
                                                ></iframe>
                                            </>
                                        </Devices>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {showDefaultMsg && !hidePreviewHeader && (
                            <>
                                <InfoText
                                    message={
                                        "This is a default Collection's item Layout preview since selected collection has no item."
                                    }
                                    theme={"success"}
                                    showInfoIcon={true}
                                    className={styles.defaultMsgPopup}
                                />
                            </>
                        )}
                    </div>
                )}
                <SidePanelInfinity
                    ref={templateOpenRef}
                    title={`${
                        templateCloneObj
                            ? "Clone & Select"
                            : viewIsClick
                            ? "Edit"
                            : "Create"
                    } Feature Group`}
                    saveButtonText={
                        templateCloneObj
                            ? "Confirm"
                            : viewIsClick
                            ? "Save"
                            : "Create"
                    }
                    onSave={async () => {
                        if (templateCloneObj) {
                            templateOpenRef?.current?.close();
                            setLoading(true);

                            const clonedItem = await dispatch(
                                cloneTemplate(templateCloneObj.id)
                            ).then(res => res.payload);

                            if (clonedItem) {
                                queryClient.removeQueries("templateListData", {
                                    exact: true,
                                });
                                setTemplateId(clonedItem?.id);
                                dispatch(
                                    toastListActions.setToastList({
                                        type: "Success",
                                        message:
                                            "Feature Group cloned successfully.",
                                        autoClose: 5000,
                                    })
                                );
                            }
                            setLoading(false);
                        } else {
                            let saveSuccessfulFlag =
                                await saveFunctionCallRef.current.onSaveHandler();
                            if (saveSuccessfulFlag) {
                                templateOpenRef.current.close();
                                setViewIsClick(false);
                                setCreatePhasePopup(false);
                            }
                        }
                    }}
                    onClose={() => {
                        setViewIsClick(false);
                        setCreatePhasePopup(false);
                        setTemplateCloneObj(null);
                    }}
                    disableSaveBtn={
                        readOnly ||
                        createTemplateState.loading ||
                        createTemplateState.mainLoader
                    }
                    children={
                        <CreateTemplate
                            editPhaseInPopUp={
                                viewIsClick || templateCloneObj ? true : false
                            }
                            // editPhaseInPopUp={false}
                            ref={saveFunctionCallRef}
                            showInPopup={true}
                            createPhaseInPopup={createPhasePopup}
                            newTemplateCreatedId={id => {
                                setTemplateId(id);
                            }}
                            isClonePanel={templateCloneObj}
                        />
                    }
                ></SidePanelInfinity>
            </div>
        );
    }
);

export default LayoutEditComp;

export const DisplayDataType = ({
    element,
    readOnly = false,
    hideDesc = false,
    mergedChoicesSet = [],
    debounceFn,
    customOnChange,
    adUnitData,
    individualLayoutData,
}) => {
    const dispatch = useDispatch();
    const addKeywordRef = useRef(null);
    // states for keywords in ad unit
    const [csvFileName, setCsvFileName] = useState("");
    const [csvFile, setCsvFile] = useState("");
    const [csvArray, setCsvArray] = useState([]);
    const [keyWordError, setCsvKeywordError] = useState(false);
    const [dropActiveKeyword, setDropActiveKeyword] = useState("");
    const [mainState, setMainState] = useState([]);
    const [mainStateCopy, setMainStateCopy] = useState([]);

    const [btnText, setBtnText] = useState("Generate Listings");
    const [keyWordLoader, setKeywordLoader] = useState(false);
    const [keywordIdFromApi, setKeywordIdFromApi] = useState("");
    const [keywordIdFromApiCopy, setKeywordIdFromApiCopy] = useState("");
    const [adUnitsChanged, setAdUnitsChanged] = useState([]);

    let isSaveClicked = false;

    useEffect(() => {
        if (
            element.type === "AdUnit" &&
            element.key === "sourceBasedListings"
        ) {
            const backgroundKeyExists =
                individualLayoutData[0]?.configurations.some(
                    data => data.key === "backgroundStaticListings"
                );

            if (backgroundKeyExists) {
                setKeywordLoader(true);
                const keyWordId =
                    individualLayoutData[0]?.configurations.filter(
                        data => data.key === "backgroundStaticListings"
                    )[0].data;

                dispatch(fetchKeyword(keyWordId)).then(data => {
                    const getKeywordObjectFromApi =
                        data?.payload?.data?.keywordMap;

                    setMainState(mapKeywordsListings(getKeywordObjectFromApi));
                    setMainStateCopy(
                        mapKeywordsListings(getKeywordObjectFromApi)
                    );
                    setKeywordLoader(false);
                });

                setKeywordIdFromApi(keyWordId);
                setKeywordIdFromApiCopy(keyWordId);
            }
        }
    }, []);

    const columns = [
        {
            text: "Keyword",
            key: "keyword",
            disableCopyText: true,
            styles: {
                width: "150px",
                minWidth: "150px",
            },
            CustomRender: ({ col, item }) => {
                const keyword = item.keyword ? item.keyword : "";
                return (
                    <TextRenderer maxWidth={200} content={keyword}>
                        {keyword}
                    </TextRenderer>
                );
            },
            maxWidth: 150,
        },
        {
            text: "Static listings",
            key: "count",
            disableCopyText: true,
            CustomRender: ({ item }) => {
                const showCountJsx =
                    item.count === 0 ? (
                        `No Job Listings`
                    ) : (
                        <CustomTooltipRenderer
                            data={mainStateCopy}
                            customRenderer={() =>
                                jobListingsPopup(item.jobsList)
                            }
                            handlePromptMouseLeave={true}
                        >
                            {item.count.toString()} Job Listings
                        </CustomTooltipRenderer>
                    );
                return item?.apiCountFetchDone
                    ? showCountJsx
                    : item.count === 0
                    ? "-"
                    : showCountJsx;
            },
        },
        {
            text: "",
            key: "",
            disableCopyText: true,
            CustomRender: ({ item }) => {
                return (
                    <ActionButtons
                        item={item}
                        deleteCallback={data => {
                            const deleteKeyword = mainStateCopy.filter(
                                item => item.keyword !== data.keyword
                            );
                            setMainStateCopy(deleteKeyword);
                            if (deleteKeyword.length > 0) {
                                setBtnText("Generate Listings");
                            } else {
                                setBtnText("Save");
                            }
                        }}
                        readOnly={readOnly}
                    />
                );
            },
            bulkActions: true,
        },
    ];

    const liDropDownPopup = data => {
        return (
            <div className={styles.tableWrapper}>
                <Table
                    loading={false}
                    columns={columns.filter(item => item.text !== "")}
                    data={mainStateCopy}
                    showBorder={true}
                    stickyHeader
                    innerScroll
                    isStickyAndNoCustomHeaderStyle={false}
                />
            </div>
        );
    };

    const jobListingsColumn = [
        {
            text: "Title",
            key: "title",
            disableCopyText: true,
            styles: {
                width: "150px",
                minWidth: "150px",
            },
            CustomRender: ({ col, item }) => {
                return (
                    <TextRenderer maxWidth={200} content={item.title}>
                        {item.title}
                    </TextRenderer>
                );
            },
            maxWidth: 150,
        },
        {
            text: "Logo",
            key: "logo",
            disableCopyText: true,
            CustomRender: ({ item }) => {
                return (
                    <>
                        {item.logo ? (
                            <div className={styles.imgWrapper}>
                                <img src={item.logo} />
                            </div>
                        ) : (
                            <div className={styles.imgWrapper}>
                                <svg
                                    width="124"
                                    height="127"
                                    viewBox="0 0 124 127"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M81.8458 27.3028L80.226 8.78887L8.78814 15.0385L13.9348 73.8682L26.5696 72.7635L24.3296 98.3658L11.9388 99.4499C9.61796 99.6527 7.57189 97.9359 7.36903 95.6153L0.0164089 11.5715C-0.187307 9.2507 1.52946 7.20463 3.85032 7.00177L83.6921 0.0162713C86.0129 -0.186546 88.059 1.53022 88.2618 3.85087L90.3789 28.0495L81.8458 27.3028ZM29.5844 38.3294C28.2015 37.6375 27.2 36.2653 27.0555 34.6122C26.8571 32.3444 28.3388 30.3172 30.4701 29.7598C30.3391 30.2409 30.2484 30.7398 30.2035 31.2531L29.5844 38.3294ZM33.088 25.6084C32.2875 26.3012 31.6162 27.1439 31.1199 28.0979L30.7179 28.1329C30.0421 28.1921 29.4094 27.7964 29.1671 27.1628L27.2296 22.1013C26.544 20.311 27.4405 18.3044 29.2308 17.6186C29.5315 17.5038 29.8474 17.4311 30.1686 17.4024C32.0784 17.2354 33.762 18.648 33.9288 20.5576C33.9575 20.8789 33.9405 21.2029 33.8795 21.5197L33.088 25.6084ZM43.7252 23.9679L39.3431 23.5847C38.3649 23.4986 37.411 23.5838 36.5109 23.8145L37.0843 22.5303C37.866 20.7794 39.9184 19.9942 41.6684 20.7749C41.9636 20.9068 42.2382 21.0782 42.485 21.2855C43.3088 21.9775 43.7306 22.9699 43.7252 23.9679ZM18.7133 23.3658C19.9455 21.8967 22.1353 21.7055 23.6034 22.9377C23.8502 23.145 24.0673 23.3855 24.2477 23.6529L27.2828 28.1437C27.6624 28.7046 27.6239 29.4503 27.1886 29.9699L26.6887 30.5649C26.2535 31.0845 25.5257 31.2514 24.9064 30.975L19.9571 28.7665C18.2072 27.9848 17.421 25.9324 18.2027 24.1815C18.3346 23.8872 18.506 23.6125 18.7133 23.3658ZM14.8303 35.6819C14.8024 35.3607 14.8186 35.0367 14.8805 34.7199C15.244 32.838 17.0648 31.6076 18.9467 31.971L24.2684 33.0004C24.9343 33.1287 25.4333 33.6833 25.4925 34.3591L25.5607 35.1327C25.619 35.8084 25.2233 36.4411 24.5906 36.6834L19.5292 38.621C19.2276 38.7358 18.9117 38.8085 18.5905 38.8373C16.6807 39.0042 14.9971 37.5916 14.8303 35.6819ZM20.7927 47.1366C20.5459 46.9293 20.3288 46.6879 20.1484 46.4213C19.075 44.8329 19.4923 42.6745 21.0808 41.6014L25.5706 38.5662C26.1324 38.1866 26.8782 38.2252 27.3978 38.6614L27.9928 39.1612C28.5115 39.5965 28.6793 40.3243 28.4029 40.9426L26.1943 45.8919C26.0624 46.1863 25.891 46.4618 25.6837 46.7086C24.4506 48.1768 22.2617 48.3687 20.7927 47.1366ZM39.3585 27.4345L119.2 34.42C121.521 34.6228 123.238 36.6689 123.034 38.9897L115.682 123.033C115.479 125.354 113.433 127.071 111.112 126.868L31.2723 119.883C28.9515 119.68 27.2349 117.634 27.4377 115.313L34.7904 31.2691C34.9932 28.9482 37.0393 27.2316 39.3601 27.4345H39.3585ZM42.8253 36.2069L37.6787 95.0366L109.117 101.286L114.263 42.4565L42.8253 36.2069ZM59.6575 60.9683C55.5957 60.6129 52.5911 57.0331 52.9465 52.9713C53.3019 48.9104 56.8827 45.9058 60.9436 46.2612C65.0054 46.6166 60.6555 49.5539 60.301 53.6148C59.9456 57.6766 63.7183 61.3237 59.6575 60.9683ZM49.3964 81.7716L61.0575 73.1779L68.299 79.3531L85.0539 65.0481C85.9397 64.2924 87.2706 64.3974 88.027 65.2832C88.1212 65.3936 88.2038 65.5129 88.2729 65.6395L99.7383 86.5897C100.297 87.611 99.9223 88.8933 98.9002 89.4525C98.5349 89.6518 98.1185 89.7397 97.7039 89.7029L50.4646 85.5703C49.3042 85.4689 48.4454 84.4459 48.5468 83.2855C48.5998 82.6815 48.9094 82.1305 49.3976 81.7716L49.3964 81.7716Z"
                                        fill="#ECF4F4"
                                    />
                                </svg>
                            </div>
                        )}
                    </>
                );
            },
        },
    ];

    const jobListingsPopup = data => {
        return (
            <div className={styles.tableWrapper}>
                <Table
                    loading={false}
                    columns={jobListingsColumn}
                    data={data}
                    showBorder={true}
                    stickyHeader
                    innerScroll
                    isStickyAndNoCustomHeaderStyle={false}
                />
            </div>
        );
    };

    const mapKeywordsListings = getKeywordObjectFromApi => {
        const fetchKeywordData = Object.keys(getKeywordObjectFromApi).map(
            (item, idx) => {
                return {
                    keyword: item,
                    count: getKeywordObjectFromApi[item]?.length,
                    apiCountFetchDone: true,
                    jobsList: getKeywordObjectFromApi[item] || [],
                };
            }
        );

        return fetchKeywordData;
    };

    return (
        <>
            {element.type === "array" && (
                <>
                    <p className="headerTitleRequired">Value</p>
                    <p className="infoText">Choice associated with an array</p>
                    <Select
                        options={Array.from(mergedChoicesSet)?.map(ele => ({
                            id: ele,
                            name: ele,
                        }))}
                        portal={false}
                        keyField={"name"}
                        onSelect={data => {
                            if (data) {
                                debounceFn(
                                    element,
                                    Array.isArray(data)
                                        ? data.map(_ => _.name)
                                        : data.name
                                );
                            }
                        }}
                        idField={"id"}
                        maintainSelectOrder={true}
                        multiSelect={element.isMultiSelect}
                        isDisabled={readOnly}
                        {...(element.isMultiSelect
                            ? {
                                  selectedIds: Array.isArray(element?.data)
                                      ? element?.data
                                      : [element?.data],
                              }
                            : {
                                  selectedId: element?.data?.toString(),
                              })}
                        onLoadSelectReturn={false}
                    />
                </>
            )}

            {element.type === "AdUnit" && (
                <>
                    <p className="headerTitleRequired">Ad Unit</p>
                    <p className="infoText">Choice associated with an array</p>

                    {element.key === "sourceBasedListings" ? (
                        <ReactSelect
                            showLabel
                            isMulti
                            maxMenuHeight={200}
                            options={
                                adUnitData?.apiData?.map(ele => ({
                                    ...ele,
                                    idName: `${ele?.name} : ${ele.adUnitId}`,
                                })) || []
                            }
                            listSelector={"idName"}
                            searchIdField={"adUnitId"}
                            selectedIds={
                                Array.isArray(element.data)
                                    ? element.data
                                    : element?.data?.adUnitIds
                            }
                            onCancelHandler={e => {
                                // onCancelHandler();
                            }}
                            customOnSelect={item => {
                                if (item) {
                                    if (element.key === "sourceBasedListings") {
                                        setBtnText("Generate Listings");
                                        if (mainStateCopy?.length > 0) {
                                            setAdUnitsChanged(element.data);
                                            addKeywordRef.current.open();
                                        }
                                    }
                                    debounceFn(
                                        element,
                                        Array.isArray(item)
                                            ? item.map(_ => _.adUnitId)
                                            : item.adUnitId,
                                        element.key === "sourceBasedListings"
                                            ? "keyword"
                                            : ""
                                    );
                                } else {
                                    // else is temporary can be removed later since the apply btn is enabled even if no changes is there in dropdown
                                    if (element.key === "sourceBasedListings") {
                                        setBtnText("Generate Listings");
                                        if (mainStateCopy?.length > 0) {
                                            setAdUnitsChanged(element.data);
                                            addKeywordRef.current.open();
                                        }
                                    }
                                }
                            }}
                            hideSelectedOptions={false}
                            saveBtnText={
                                mainStateCopy?.length > 0
                                    ? "Go to Keyword"
                                    : "Apply"
                            }
                            isLoading={adUnitData.isLoading}
                        />
                    ) : (
                        <Select
                            options={
                                adUnitData?.apiData?.map(ele => ({
                                    ...ele,
                                    idName: `${ele?.name} : ${ele.adUnitId}`,
                                })) || []
                            }
                            portal={false}
                            keyField={"idName"}
                            onSelect={data => {
                                if (data) {
                                    debounceFn(
                                        element,
                                        Array.isArray(data)
                                            ? data.map(_ => _.adUnitId)
                                            : data.adUnitId,
                                        element.key === "sourceBasedListings"
                                            ? "keyword"
                                            : ""
                                    );
                                }
                            }}
                            idField={"adUnitId"}
                            loading={adUnitData.isLoading}
                            isDisabled={readOnly}
                            maintainSelectOrder={true}
                            multiSelect={element.isMultiSelect}
                            {...(element.isMultiSelect
                                ? {
                                      selectedIds: Array.isArray(element?.data)
                                          ? element?.data
                                          : [element?.data],
                                  }
                                : {
                                      selectedId: element?.data?.toString(),
                                  })}
                            onLoadSelectReturn={false}
                        />
                    )}

                    {element.key === "sourceBasedListings" && (
                        <>
                            <div className="dp-parent mt-10">
                                <div className="headerTitleRequired">
                                    Keyword :&nbsp;
                                </div>
                                <div
                                    className="dp-parent"
                                    onClick={() => {
                                        addKeywordRef.current.open();
                                    }}
                                >
                                    {keyWordLoader ? (
                                        <div>
                                            <Skeleton width={140} height={15} />
                                        </div>
                                    ) : mainStateCopy.length > 0 ? (
                                        <CustomTooltipRenderer
                                            data={mainStateCopy}
                                            customRenderer={() =>
                                                liDropDownPopup(mainStateCopy)
                                            }
                                            handlePromptMouseLeave={true}
                                        >
                                            <div
                                                className={`${styles.keywordTextWrap} dp-parent`}
                                            >
                                                <div>
                                                    {mainStateCopy[0]?.keyword}
                                                    &nbsp;
                                                </div>
                                                {mainStateCopy.length > 1 && (
                                                    <div className="link">
                                                        +{" "}
                                                        {mainStateCopy.length -
                                                            1}
                                                    </div>
                                                )}
                                            </div>
                                        </CustomTooltipRenderer>
                                    ) : (
                                        <div className="dp-parent">
                                            <div className="link">
                                                Add Keyword{" "}
                                            </div>
                                            <Tooltip
                                                message={
                                                    "Generate static content for keywords"
                                                }
                                                position="top"
                                            >
                                                <div
                                                    className={`ml-10 circleIconColor circleIconColor--darkHover`}
                                                >
                                                    <Icon
                                                        icon="Info"
                                                        size="18"
                                                    />
                                                </div>
                                            </Tooltip>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}

            {element.type === "number" && (
                <>
                    <p className="headerTitleRequired">
                        Value:{" "}
                        {!hideDesc
                            ? `(Enter default value between ${element?.min} and ${element?.max})`
                            : ""}
                    </p>
                    <p className="infoText">Default value for number type</p>
                    <Input
                        value={element?.data}
                        className="maxChildContainer"
                        placeholder={`Enter value`}
                        required={true}
                        type="number"
                        name={element.data}
                        onChange={e => {
                            if (typeof customOnChange === "function") {
                                customOnChange(element, e.target.value);
                            } else {
                                debounceFn(element, e.target.value);
                            }
                        }}
                        disabled={readOnly}
                    ></Input>
                </>
            )}
            {element.type === "boolean" && (
                <>
                    <p className="headerTitleRequired">Value:</p>
                    <p className="infoText">Boolean property</p>
                    <div className="dp-parent">
                        <ToggleValueSwitch
                            leftLabel={"False"}
                            rightLabel={"True"}
                            toggleState={element.data}
                            onChange={val => {
                                if (typeof customOnChange === "function") {
                                    customOnChange(element, val);
                                } else {
                                    debounceFn(element, val);
                                }
                            }}
                            isDisabled={readOnly}
                        />
                    </div>
                </>
            )}
            {element.type === "richText" && (
                <>
                    <p className="headerTitleRequired">Value</p>
                    <p className="infoText">Default value for string type</p>
                    {readOnly ? (
                        <div>{element.data}</div>
                    ) : (
                        <RichTextBox
                            htmlContent={element.data || ""}
                            customOnBlur={data => {
                                debounceFn(element, data);
                            }}
                            customOptions={{
                                macro: false,
                            }}
                            containDropdown
                        />
                    )}
                </>
            )}
            {element.type === "string" && (
                <>
                    <p className="headerTitleRequired">Value:</p>
                    <p className="infoText">Default value for rich text type</p>
                    {readOnly ? (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: element.data || "",
                            }}
                        ></div>
                    ) : (
                        <Input
                            value={element?.data}
                            className="maxChildContainer"
                            placeholder={`Enter value`}
                            required
                            name={element.data}
                            onChange={e => {
                                if (typeof customOnChange === "function") {
                                    customOnChange(element, e.target.value);
                                } else {
                                    debounceFn(element, e.target.value);
                                }
                            }}
                            disabled={readOnly}
                        ></Input>
                    )}
                </>
            )}
            <SidePanelInfinity
                ref={addKeywordRef}
                title={"Add Keyword"}
                saveButtonText={btnText}
                onSave={async () => {
                    if (mainStateCopy.length > 0) {
                        if (btnText === "Generate Listings") {
                            setKeywordLoader(true);
                            dispatch(
                                saveKeyword({
                                    adUnitIds:
                                        element?.data?.adUnitIds ||
                                        element?.data,
                                    keywords: mainStateCopy.map(
                                        item => item?.keyword || item
                                    ),
                                })
                            ).then(data => {
                                setKeywordIdFromApi(data?.payload?.id || "");
                                setBtnText("Save");
                                setKeywordLoader(false);
                                const getKeywordObjectFromApi =
                                    data?.payload?.keywordMap;

                                setMainStateCopy(
                                    mapKeywordsListings(getKeywordObjectFromApi)
                                );
                                dispatch(
                                    toastListActions.setToastList({
                                        type: "Success",
                                        message: "Static listings generated.",
                                        autoClose: 2000,
                                    })
                                );
                            });
                        } else {
                            isSaveClicked = true;
                            if (
                                keywordIdFromApiCopy !== keywordIdFromApi &&
                                mainStateCopy?.length > 0
                            ) {
                                debounceFn(
                                    element,
                                    keywordIdFromApi,
                                    "keyword",
                                    "id"
                                );
                                setMainState(mainStateCopy);
                                dispatch(
                                    toastListActions.setToastList({
                                        type: "Success",
                                        message: "Keywords added.",
                                        autoClose: 2000,
                                    })
                                );
                            }
                            addKeywordRef.current.close();
                        }
                    } else {
                        isSaveClicked = true;
                        setMainState([]);
                        debounceFn(
                            element,
                            element.data,
                            "keyword",
                            "keywordsRemoved"
                        );
                        addKeywordRef.current.close();
                    }
                }}
                onClose={() => {
                    if (!isSaveClicked) {
                        setMainStateCopy(mainState);
                        setCsvFileName("");
                        setCsvFile("");
                        setCsvArray([]);
                        setCsvKeywordError(false);
                        setDropActiveKeyword("");
                        if (mainStateCopy.length > 0) {
                            setBtnText("Generate Listings");
                        } else {
                            setBtnText("Save");
                        }
                        if (adUnitsChanged?.length > 0) {
                            dispatch(
                                toastListActions.setToastList({
                                    type: "Error",
                                    message: "Ad units set as earlier state.",
                                })
                            );
                            debounceFn(
                                element,
                                adUnitsChanged,
                                element.key === "sourceBasedListings"
                                    ? "keyword"
                                    : ""
                            );
                            setAdUnitsChanged([]);
                        }
                    }
                }}
                disableSaveBtn={keyWordLoader}
                children={
                    <>
                        <div>
                            <div className="mb-10">
                                By adding keywords we will generate a static
                                listing for each keyword
                            </div>
                            <TargettingParamsWrapperComp
                                item={{
                                    icon: "KeyWord",
                                    name: "Keyword",
                                    id: "Keyword",
                                }}
                                key={"Keyword"}
                                header={"Keyword"}
                                resetFunction={data => {
                                    setCsvFileName("");
                                    setCsvFile("");
                                    setCsvArray([]);
                                    setCsvKeywordError(false);
                                    setDropActiveKeyword("");
                                    setMainStateCopy([]);
                                    setBtnText("Save");
                                }}
                                readOnly={readOnly}
                                resetTooltipMsg={"Reset Keyword"}
                            >
                                <InputList
                                    dynamicInputAndCsvUploaderType={true}
                                    uploadMsg={"keyword"}
                                    type="ClientIPsType"
                                    title="Keyword"
                                    placeholderOne={"Enter Keyword"}
                                    csvFileName={csvFileName}
                                    setCsvFileName={data =>
                                        setCsvFileName(data)
                                    }
                                    csvFile={csvFile}
                                    setCsvFile={data => setCsvFile(data)}
                                    csvArray={csvArray}
                                    setCsvArray={data => setCsvArray(data)}
                                    error={keyWordError}
                                    setError={data => setCsvKeywordError(data)}
                                    onDropActiveclientIPs={dropActiveKeyword}
                                    setonDropActiveclientIPs={data =>
                                        setDropActiveKeyword(data)
                                    }
                                    mainState={mainStateCopy}
                                    mainStateDataHide={true}
                                    setMainState={data => {
                                        setMainStateCopy(
                                            data.map((item, idx) => {
                                                return {
                                                    keyword:
                                                        item?.keyword || item,
                                                    count: item.count || 0,
                                                    ...(item.apiCountFetchDone
                                                        ? {
                                                              apiCountFetchDone: true,
                                                          }
                                                        : {}),
                                                };
                                            })
                                        );
                                        setBtnText("Generate Listings");
                                    }}
                                    readOnly={readOnly}
                                    customValidation={data => {
                                        return mainStateCopy.some(
                                            item => item?.keyword === data
                                        );
                                    }}
                                />
                            </TargettingParamsWrapperComp>
                            {mainStateCopy?.length > 0 && (
                                <div
                                    className={`row revenueTable ${styles.tableWrapper}`}
                                >
                                    <Table
                                        loading={false}
                                        columns={columns}
                                        data={mainStateCopy}
                                        showBorder={true}
                                        stickyHeader
                                        innerScroll
                                        additionalProps={{
                                            mainStateCopy: mainStateCopy,
                                        }}
                                        isStickyAndNoCustomHeaderStyle={false}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                }
            ></SidePanelInfinity>
        </>
    );
};
